import {
    useRef,
    useState,
    useEffect,
    useCallback,
    useContext,
    useMemo,
    createContext,
    type ReactNode,
} from "react";
import ReactDOM from "react-dom";
import { Button } from "@timeedit/ui-components/components/Button/Button";
import { CloseOutlined } from "@timeedit/ui-components/components/Icon/Icons/index";
import Tooltip from "@timeedit/ui-components/components/Tooltip/Tooltip";
import Icon from "@timeedit/ui-components/components/Icon/Icon";
import Language from "../lib/Language";
import { TOOLTIP_DELAY, TOPBAR_HEIGHT } from "../lib/UIConstants";
import { Typography } from "@timeedit/ui-components/antd";

type TFloatingWindowProps = {
    icon?: React.JSX.Element;
    title: string;
    onClose: () => void;
    children: ReactNode;
    initialX?: number;
    initialY?: number;
    initialWidth?: number;
    initialHeight?: number;
    minWidth?: number;
    minHeight?: number;
    helpText?: React.JSX.Element;
    showCloseButton?: boolean;
};

// Options accepted by appContext.presentFloatingWindow. Same as the component props,
// except content is passed explicitly and onClose is optional. onClose runs before
// the window is removed; returning false keeps the window open (e.g. when the user
// declines an unsaved-changes confirmation).
export type TFloatingWindowOptions = Omit<TFloatingWindowProps, "children" | "onClose"> & {
    windowKey?: string;
    content: ReactNode;
    onClose?: () => boolean | void;
    showCloseButton?: boolean;
};

export type TFloatingWindowEntry = TFloatingWindowOptions & { windowKey: string };

// Mounted windows in mount order, so Escape only closes the top-most one.
const escapeStack: number[] = [];
let nextEscapeId = 0;

// Keep initial sizes inside the viewport, and keep at least this much of the
// window reachable when dragging so it can always be grabbed again.
const VIEWPORT_MARGIN = 40;
const DRAG_MIN_VISIBLE = 80;

// Mirrors Mousetrap's stopCallback: targets whose own Escape behavior
// (closing a dropdown, aborting input) should win over closing the window.
const isEditableTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
    );
};

// A FloatingWindow can host a footer, rendered as a separate strip below the
// scrolling content (like the button row of a classic dialog). Because the
// window's content is supplied as opaque `children`, the footer is filled via a
// portal: content renders a <FloatingWindowFooter> anywhere in its tree and its
// children are teleported into the window's footer slot. footerEl is the portal
// target; setFooterActive lets the window know a footer exists so it only draws
// the separator/padding when there is something to show.
type TFloatingWindowFooterContext = {
    footerEl: HTMLElement | null;
    setFooterActive: (active: boolean) => void;
};

const FloatingWindowFooterContext = createContext<TFloatingWindowFooterContext | null>(null);

// Renders its children into the enclosing FloatingWindow's footer slot. Renders
// nothing when used outside a FloatingWindow.
export const FloatingWindowFooter = ({ children }: { children: ReactNode }) => {
    const context = useContext(FloatingWindowFooterContext);
    const setFooterActive = context?.setFooterActive;
    useEffect(() => {
        setFooterActive?.(true);
        return () => setFooterActive?.(false);
    }, [setFooterActive]);
    if (!context?.footerEl) {
        return null;
    }
    return ReactDOM.createPortal(children, context.footerEl);
};

const FloatingWindow = ({
    icon,
    title,
    onClose,
    children,
    initialX,
    initialY,
    helpText,
    initialWidth = 360,
    initialHeight = 400,
    minWidth = 260,
    minHeight = 200,
    showCloseButton = true,
}: TFloatingWindowProps) => {
    const [size, setSize] = useState(() => ({
        width: Math.min(initialWidth, Math.max(minWidth, window.innerWidth - VIEWPORT_MARGIN)),
        height: Math.min(
            initialHeight,
            Math.max(minHeight, window.innerHeight - TOPBAR_HEIGHT - VIEWPORT_MARGIN)
        ),
    }));
    const [position, setPosition] = useState(() => ({
        x: initialX ?? Math.max(0, Math.round((window.innerWidth - size.width) / 2)),
        y: Math.max(TOPBAR_HEIGHT, initialY ?? Math.round(window.innerHeight * 0.15)),
    }));

    // Footer portal target and whether any <FloatingWindowFooter> is currently
    // mounted in the content. When nothing uses the footer the slot stays empty
    // and adds no separator or height, so plain windows look exactly as before.
    const [footerEl, setFooterEl] = useState<HTMLElement | null>(null);
    const [hasFooter, setHasFooter] = useState(false);
    const footerContext = useMemo<TFloatingWindowFooterContext>(
        () => ({ footerEl, setFooterActive: setHasFooter }),
        [footerEl]
    );

    const dragRef = useRef<{
        startX: number;
        startY: number;
        startPosX: number;
        startPosY: number;
        width: number;
    } | null>(null);
    const resizeRef = useRef<{
        startX: number;
        startY: number;
        startW: number;
        startH: number;
        startPosX: number;
        corner: "bottom-left" | "bottom-right";
    } | null>(null);

    const handleDragMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragRef.current = {
                startX: e.clientX,
                startY: e.clientY,
                startPosX: position.x,
                startPosY: position.y,
                width: size.width,
            };
        },
        [position, size.width]
    );

    const handleResizeMouseDown = useCallback(
        (corner: "bottom-left" | "bottom-right") => (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            resizeRef.current = {
                startX: e.clientX,
                startY: e.clientY,
                startW: size.width,
                startH: size.height,
                startPosX: position.x,
                corner,
            };
        },
        [size, position]
    );

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (dragRef.current) {
                const dx = e.clientX - dragRef.current.startX;
                const dy = e.clientY - dragRef.current.startY;
                setPosition({
                    x: Math.min(
                        Math.max(
                            dragRef.current.startPosX + dx,
                            DRAG_MIN_VISIBLE - dragRef.current.width
                        ),
                        window.innerWidth - DRAG_MIN_VISIBLE
                    ),
                    y: Math.max(TOPBAR_HEIGHT, dragRef.current.startPosY + dy),
                });
            }
            if (resizeRef.current) {
                const dx = e.clientX - resizeRef.current.startX;
                const dy = e.clientY - resizeRef.current.startY;
                const newHeight = Math.max(minHeight, resizeRef.current.startH + dy);
                if (resizeRef.current.corner === "bottom-left") {
                    const newWidth = Math.max(minWidth, resizeRef.current.startW - dx);
                    const clampedDx = resizeRef.current.startW - newWidth;
                    const xPos = resizeRef.current.startPosX + clampedDx;
                    setSize({ width: newWidth, height: newHeight });
                    setPosition((prev) => ({
                        ...prev,
                        x: xPos,
                    }));
                } else {
                    setSize({
                        width: Math.max(minWidth, resizeRef.current.startW + dx),
                        height: newHeight,
                    });
                }
            }
        };

        const handleMouseUp = () => {
            dragRef.current = null;
            resizeRef.current = null;
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [minWidth, minHeight]);

    const escapeIdRef = useRef<number | null>(null);
    useEffect(() => {
        const id = nextEscapeId++;
        escapeIdRef.current = id;
        escapeStack.push(id);
        return () => {
            const index = escapeStack.indexOf(id);
            if (index !== -1) {
                escapeStack.splice(index, 1);
            }
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") {
                return;
            }
            if (escapeStack[escapeStack.length - 1] !== escapeIdRef.current) {
                return;
            }
            // A modal dialog stacks above all floating windows; Escape belongs to it.
            if (document.querySelector(".modalOverlay")) {
                return;
            }
            if (isEditableTarget(e.target)) {
                return;
            }
            // Capture phase, so document-level shortcut bindings (Mousetrap)
            // never see the Escape that is directed at this window.
            e.stopPropagation();
            onClose();
        };
        window.addEventListener("keydown", handleKeyDown, true);
        return () => window.removeEventListener("keydown", handleKeyDown, true);
    }, [onClose]);

    return ReactDOM.createPortal(
        <div
            style={{
                position: "fixed",
                left: position.x,
                top: position.y,
                width: size.width,
                height: size.height,
                display: "flex",
                flexDirection: "column",
                background: "#fff",
                borderRadius: 8,
                boxShadow:
                    "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
                // Below .modal (107) and .modalOverlay (108) so true modals always
                // cover floating windows; above the rest of the app via portal order.
                zIndex: 106,
                overflow: "hidden",
            }}
        >
            <div
                onMouseDown={handleDragMouseDown}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 8px 6px 12px",
                    cursor: "move",
                    userSelect: "none",
                    flexShrink: 0,
                }}
            >
                <div style={{ display: "flex", gap: "8px" }}>
                    {icon}
                    {helpText ? (
                        <Tooltip mouseEnterDelay={TOOLTIP_DELAY} title={helpText}>
                            <Typography.Title style={{ marginBottom: 0 }} level={5}>
                                {title}{" "}
                                {
                                    <span>
                                        <Icon
                                            id="QuestionCircleOutlined"
                                            alt={Language.get("menu_help")}
                                        />
                                    </span>
                                }
                            </Typography.Title>
                        </Tooltip>
                    ) : (
                        <Typography.Title style={{ marginBottom: 0 }} level={5}>
                            {title}
                        </Typography.Title>
                    )}
                </div>
                {showCloseButton ? (
                    <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined alt={Language.get("menu_close")} />}
                        onClick={onClose}
                    />
                ) : null}
            </div>
            <FloatingWindowFooterContext.Provider value={footerContext}>
                <div
                    style={{
                        flex: 1,
                        overflow: "auto",
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        paddingBottom: "8px",
                    }}
                >
                    {children}
                </div>
            </FloatingWindowFooterContext.Provider>
            <div
                ref={setFooterEl}
                style={{
                    flexShrink: 0,
                    ...(hasFooter ? { borderTop: "1px solid #f0f0f0", padding: "10px 12px" } : {}),
                }}
            />
            <div
                onMouseDown={handleResizeMouseDown("bottom-left")}
                style={{
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    width: 16,
                    height: 16,
                    cursor: "nesw-resize",
                }}
            />
            <div
                onMouseDown={handleResizeMouseDown("bottom-right")}
                style={{
                    position: "absolute",
                    right: 0,
                    bottom: 0,
                    width: 16,
                    height: 16,
                    cursor: "nwse-resize",
                }}
            />
        </div>,
        document.body
    );
};

export default FloatingWindow;
